import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { buildExactProductRequestUrl } from '@theme/discovery-authority-state';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  #requestGeneration = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#requestGeneration += 1;
    this.#abortController?.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const isInDirectProductRegion = Boolean(
      this.closest('[data-testid="product-information"]') && !event.target.closest('product-card')
    );
    const directProductSectionSelector = this.dataset.sectionId
      ? `#shopify-section-${CSS.escape(this.dataset.sectionId)}`
      : '[data-testid="product-information"]';

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : isInDirectProductRegion
      ? directProductSectionSelector
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '', connectedProductId = '' } =
      selectedOption.dataset;
    // Every selection is pending-deny until the Section Rendering response proves this exact variant is allowed.
    const previousLaunchAllowed = this.dataset.launchAllowed === 'true';
    this.dataset.launchAllowed = 'false';
    delete this.dataset.approvedVariantId;
    const authorityCarrier = this.closest('[data-product-grid-content], product-card');
    if (authorityCarrier instanceof HTMLElement) {
      authorityCarrier.dataset.launchAllowed = 'false';
      delete authorityCarrier.dataset.approvedVariantId;
    }
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      morphOnAuthorityChangeOnly: isInDirectProductRegion && !loadsNewProduct,
      previousLaunchAllowed,
      detail: { optionValueId, variantId, connectedProductUrl, connectedProductId },
    });

  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const viewParamValue = getViewParameterValue();
    let exactVariantId = '';
    let optionValueIds = [];

    if (source === 'product-card') {
      exactVariantId =
        selectedOption.dataset.variantId || selectedOption.dataset.firstAvailableOrFirstVariantId || '';
      if (!exactVariantId) {
        optionValueIds = this.selectedOptionsValues.length
          ? sourceSelectedOptionsValues
          : [selectedOption.dataset.optionValueId || ''].filter(Boolean);
      }
    } else if (selectedOption.dataset.connectedProductUrl) {
      exactVariantId = selectedOption.dataset.variantId || '';
    } else if (this.selectedOptionsValues.length) {
      optionValueIds = this.selectedOptionsValues;
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    return buildExactProductRequestUrl({
      productUrl: productUrl || '',
      sectionId: closestSectionId ? SECTION_ID_MAP[closestSectionId] || '' : '',
      variantId: exactVariantId,
      optionValueIds,
      view: viewParamValue || '',
    });
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [options.morphOnAuthorityChangeOnly] - Only morph the broad boundary when allow/deny authority changes.
   * @param {boolean} [options.previousLaunchAllowed] - Approval state before this request entered pending-deny.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, connectedProductId?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(
    requestUrl,
    { morphElementSelector, morphOnAuthorityChangeOnly = false, previousLaunchAllowed = false, detail = {} } = {}
  ) {
    const { optionValueId = '', variantId, connectedProductUrl = '', connectedProductId = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const generation = ++this.#requestGeneration;
    const selectedOptionIdAtRequest = this.selectedOptionId;

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          connectedProductId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: controller.signal, cache: 'no-store' })
      .then((response) => {
        if (generation !== this.#requestGeneration || controller.signal.aborted) {
          throw new DOMException('Stale variant response', 'AbortError');
        }
        return response.text();
      })
      .then((responseText) => {
        if (generation !== this.#requestGeneration || controller.signal.aborted) {
          throw new DOMException('Stale variant response', 'AbortError');
        }
        if (this.#abortController === controller) this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const exactSectionSelector = this.dataset.sectionId
          ? `#shopify-section-${CSS.escape(this.dataset.sectionId)}`
          : '';
        const responseAuthorityBoundary =
          morphElementSelector === 'main'
            ? exactSectionSelector
              ? html.querySelector(`main ${exactSectionSelector}`)
              : null
            : morphElementSelector
            ? html.querySelector(morphElementSelector)
            : null;
        const responseSearchRoot = responseAuthorityBoundary ?? (morphElementSelector ? null : html);
        const responseVariantPicker = responseSearchRoot?.querySelector('variant-picker') ?? null;
        const responseCarrier =
          responseVariantPicker?.closest('[data-product-grid-content], product-card') ??
          (responseAuthorityBoundary?.matches('[data-product-grid-content], [data-benegrove-commerce-gate]')
            ? responseAuthorityBoundary
            : responseAuthorityBoundary?.querySelector(
                '[data-product-grid-content], [data-benegrove-commerce-gate]'
              ));
        const variantAuthorityScript =
          responseVariantPicker?.querySelector('script[type="application/json"]') ??
          responseCarrier?.querySelector('script[data-benegrove-exact-variant][type="application/json"]');
        const textContent = variantAuthorityScript?.textContent;
        const responseLaunchAllowed =
          responseVariantPicker?.getAttribute('data-launch-allowed') === 'true' ||
          variantAuthorityScript?.getAttribute('data-launch-allowed') === 'true';
        const approvedVariantId =
          responseVariantPicker?.getAttribute('data-approved-variant-id')?.trim() ??
          variantAuthorityScript?.getAttribute('data-approved-variant-id')?.trim() ??
          '';
        const carrierLaunchAllowed = responseCarrier?.getAttribute('data-launch-allowed') === 'true';
        const carrierApprovedVariantId = responseCarrier?.getAttribute('data-approved-variant-id')?.trim() ?? '';
        const carrierProductId = responseCarrier?.getAttribute('data-product-id')?.trim() ?? '';
        const authorityProductId =
          responseVariantPicker?.getAttribute('data-product-id')?.trim() ??
          variantAuthorityScript?.getAttribute('data-product-id')?.trim() ??
          '';
        const isCardSwatches = this.tagName.toLowerCase() === 'swatches-variant-picker-component';
        const expectedProductId = connectedProductId || this.dataset.productId || '';
        const intendedVariantId = String(variantId || '');
        const exactProductIdentity =
          expectedProductId !== '' &&
          carrierProductId === expectedProductId &&
          authorityProductId === expectedProductId;
        const sectionScopedExactProductDeny =
          responseAuthorityBoundary !== null &&
          !responseVariantPicker &&
          responseCarrier?.matches('[data-benegrove-commerce-gate]') === true &&
          expectedProductId !== '' &&
          carrierProductId === expectedProductId;
        const sectionScopedDenyWithoutProductId =
          responseAuthorityBoundary !== null &&
          !responseVariantPicker &&
          responseCarrier?.matches('[data-benegrove-commerce-gate]') === true &&
          carrierProductId === '';

        let variantData = null;
        let responseJsonParsed = false;
        if (textContent) {
          try {
            variantData = JSON.parse(textContent);
            responseJsonParsed = true;
          } catch {
            variantData = null;
          }
        }

        const exactAllowed =
          Boolean(selectedOptionIdAtRequest) &&
          responseLaunchAllowed &&
          carrierLaunchAllowed &&
          exactProductIdentity &&
          intendedVariantId !== '' &&
          approvedVariantId !== '' &&
          approvedVariantId === intendedVariantId &&
          carrierApprovedVariantId === approvedVariantId &&
          variantData &&
          typeof variantData === 'object' &&
          variantData.available === true &&
          String(variantData.id ?? '') === approvedVariantId;
        const exactDenied =
          (exactProductIdentity || sectionScopedExactProductDeny || sectionScopedDenyWithoutProductId) &&
          responseCarrier?.getAttribute('data-launch-allowed') === 'false' &&
          (!responseVariantPicker ||
            (responseVariantPicker.getAttribute('data-launch-allowed') === 'false' &&
              responseJsonParsed &&
              variantData === null));

        let newProduct;

        // Only an explicit exact allow or explicit fail-closed denial may replace live DOM.
        // Missing markers, malformed JSON, and mismatched IDs leave the pending-deny UI in place.
        if (exactAllowed || exactDenied) {
          const useBroadMorph =
            Boolean(morphElementSelector) &&
            (!morphOnAuthorityChangeOnly || previousLaunchAllowed !== Boolean(exactAllowed));

          if (useBroadMorph && morphElementSelector === 'main') {
            this.updateMain(html);
          } else if (useBroadMorph && morphElementSelector) {
            this.updateElement(html, morphElementSelector, exactDenied);
          } else if (responseVariantPicker) {
            const { overflowList } = this.refs;
            const wasSwatchesExpanded =
              overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

            const hasMatchingPicker = html.querySelector(this.tagName.toLowerCase()) !== null;
            if (hasMatchingPicker) {
              newProduct = this.updateVariantPicker(html);
            } else if (isCardSwatches && exactAllowed) {
              if (carrierProductId !== this.dataset.productId) {
                newProduct = { id: carrierProductId, url: connectedProductUrl };
              }
              this.dataset.productId = carrierProductId;
              if (connectedProductUrl) this.dataset.productUrl = connectedProductUrl;
            } else if (!isCardSwatches) {
              throw new Error('No matching variant picker source found');
            }

            if (wasSwatchesExpanded) {
              const overflowListAfterMorph = overflowList;
              if (overflowListAfterMorph instanceof OverflowList) {
                overflowListAfterMorph.showAll();
              }
            }
          }
        }

        // Resolve the ProductSelectEvent promise only when the server marker, JSON, and exact ID agree.
        if (exactAllowed) {
          this.dataset.launchAllowed = 'true';
          this.dataset.approvedVariantId = approvedVariantId;
          const productViewAttr = variantAuthorityScript
            ?.closest('[view-event-payload]')
            ?.getAttribute('view-event-payload')
            ?.trim();
          let selectedVariantEvent = null;
          try {
            selectedVariantEvent = (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null;
          } catch {
            selectedVariantEvent = null;
          }

          deferredEventPromise.resolve({
            variant: selectedVariantEvent,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              sourceId: selectedOptionIdAtRequest,
              resource: variantData,
            },
          });

          return;
        }

        this.dataset.launchAllowed = 'false';
        delete this.dataset.approvedVariantId;

        if (exactDenied) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              sourceId: selectedOptionIdAtRequest,
              resource: null,
            },
          });
          return;
        }

        throw new Error('Non-authoritative variant response');
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (this.#abortController === controller) {
          this.#abortController = undefined;
          this.#pendingRequestUrl = undefined;
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   * @param {boolean} [allowRemoval] - Whether an explicit denied response may remove a missing replacement.
   */
  updateElement(newHtml, elementSelector, allowRemoval = false) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    if (!newElement) {
      if (allowRemoval) {
        element.remove();
        return;
      }
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
